import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import type { STATUS } from 'types/layout';

import { useBankProfile } from '../contexts/BankProfileContext';
import { useAuth } from './useAuth';

export interface StatementFiltersProps {
	iban?: string;
	type?: string[];
}

interface AllRequestedStatementsProps {
	currentPage: number;
	limit?: number;
	filters?: StatementFiltersProps;
}

// ToDo: old statements still do not have account_uuid so account_id is fallback for download functionality (use account ID in case UUID is missing in download link)
export interface StatementProps {
	account_uuid: string;
	account_id: string;
	created_at: string;
	date_from: string;
	date_to: string;
	id: string;
	status: STATUS;
	type: 'csv' | 'pdf' | 'xml' | 'xlsx';
	updated_at: string;
	account_iban: string;
	account_name: string;
}
interface PaginationMeta {
	count: number;
	current_page: number;
	has_next_page: boolean;
	limit: number;
	offset: number;
}

interface StatementsResponseProps {
	items: StatementProps[];
	pagination: PaginationMeta;
}

const useAllRequestedStatements = ({
	currentPage,
	limit = 15,
	filters,
}: AllRequestedStatementsProps) => {
	const { session } = useAuth();
	const { selectedProfile } = useBankProfile();
	const offset = (currentPage - 1) * limit;

	const params = {
		offset,
		currentPage,
		per_page: limit,
		...(filters?.iban ? { 'filter[account_iban]': filters.iban } : {}),
		...(filters?.type?.length ? { 'filter[type]': filters.type } : {}),
	};

	const fetchGeneratedStatements = async (): Promise<StatementsResponseProps> => {
		const response = await axios.get(
			`${import.meta.env.VITE_API_HOST}/api/v1/profiles/${selectedProfile.id}/statement`,
			{
				headers: { Authorization: session?.tokenized },
				params,
			},
		);

		return response.data;
	};

	return useQuery({
		queryKey: ['generated-statements', selectedProfile?.id, currentPage, limit, filters],
		queryFn: fetchGeneratedStatements,
		enabled: !!selectedProfile?.id && !!session?.tokenized,
		refetchOnWindowFocus: 'always',
		staleTime: 1000,
	});
};

export { useAllRequestedStatements };
